import type {
  ChartData,
  ChartMetaInfo,
  ChartTemplate,
  ChartTemplateContent,
  IExternalSaveLoadAdapter,
  LineToolsAndGroupsLoadRequestContext,
  LineToolsAndGroupsLoadRequestType,
  LineToolsAndGroupsState,
  StudyTemplateData,
  StudyTemplateMetaInfo,
} from 'charting_library';
import localforage from 'localforage';

interface StoredChart {
  id: string;
  name: string;
  symbol: string;
  resolution: string;
  content: string;
  timestamp: number;
}

interface StoredStudyTemplate {
  name: string;
  content: string;
}

interface StoredDrawingTemplate {
  toolName: string;
  templateName: string;
  content: string;
}

interface StoredChartTemplate {
  name: string;
  content: ChartTemplateContent;
}

interface StoredLineTools {
  layoutId: string | undefined;
  chartId: string | number;
  state: LineToolsAndGroupsState;
}

const createStore = (name: string, storeName: string) =>
  localforage.createInstance({ name, storeName });

const chartsStore = createStore('tv-charts', 'charts');
const studyTemplatesStore = createStore('tv-study-templates', 'templates');
const drawingTemplatesStore = createStore('tv-drawing-templates', 'templates');
const chartTemplatesStore = createStore('tv-chart-templates', 'templates');
const lineToolsStore = createStore('tv-line-tools', 'drawings');

const generateId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const buildKey = (scope: string, ...parts: (string | number | undefined)[]) =>
  [scope, ...parts.filter(p => p !== undefined)].join(':');

export function createSaveLoadAdapter(scope: string): IExternalSaveLoadAdapter {
  const adapter: IExternalSaveLoadAdapter = {
    async getAllCharts(): Promise<ChartMetaInfo[]> {
      try {
        const charts: ChartMetaInfo[] = [];
        await chartsStore.iterate<StoredChart, void>((value, key) => {
          if (key.startsWith(`${scope}:`)) {
            charts.push({
              id: value.id,
              name: value.name,
              symbol: value.symbol,
              resolution: value.resolution as ChartMetaInfo['resolution'],
              timestamp: value.timestamp,
            });
          }
        });
        return charts.sort((a, b) => b.timestamp - a.timestamp);
      } catch (error) {
        console.error('[SaveLoadAdapter] getAllCharts failed:', error);
        return [];
      }
    },

    async saveChart(chartData: ChartData): Promise<string | number> {
      try {
        const id = chartData.id?.toString() || generateId();
        const stored: StoredChart = {
          id,
          name: chartData.name,
          symbol: chartData.symbol,
          resolution: chartData.resolution,
          content: chartData.content,
          timestamp: chartData.timestamp || Date.now(),
        };
        await chartsStore.setItem(buildKey(scope, id), stored);
        return id;
      } catch (error) {
        console.error('[SaveLoadAdapter] saveChart failed:', error);
        return chartData.id || generateId();
      }
    },

    async getChartContent(chartId: string | number): Promise<string> {
      try {
        const stored = await chartsStore.getItem<StoredChart>(buildKey(scope, chartId));
        return stored?.content || '';
      } catch (error) {
        console.error('[SaveLoadAdapter] getChartContent failed:', error);
        return '';
      }
    },

    async removeChart(id: string | number): Promise<void> {
      try {
        await chartsStore.removeItem(buildKey(scope, id));
      } catch (error) {
        console.error('[SaveLoadAdapter] removeChart failed:', error);
      }
    },

    async getAllStudyTemplates(): Promise<StudyTemplateMetaInfo[]> {
      try {
        const templates: StudyTemplateMetaInfo[] = [];
        await studyTemplatesStore.iterate<StoredStudyTemplate, void>((value, key) => {
          if (key.startsWith(`${scope}:`)) {
            templates.push({ name: value.name });
          }
        });
        return templates;
      } catch (error) {
        console.error('[SaveLoadAdapter] getAllStudyTemplates failed:', error);
        return [];
      }
    },

    async saveStudyTemplate(studyTemplateData: StudyTemplateData): Promise<void> {
      try {
        const stored: StoredStudyTemplate = {
          name: studyTemplateData.name,
          content: studyTemplateData.content,
        };
        await studyTemplatesStore.setItem(buildKey(scope, studyTemplateData.name), stored);
      } catch (error) {
        console.error('[SaveLoadAdapter] saveStudyTemplate failed:', error);
      }
    },

    async getStudyTemplateContent(studyTemplateInfo: StudyTemplateMetaInfo): Promise<string> {
      try {
        const stored = await studyTemplatesStore.getItem<StoredStudyTemplate>(
          buildKey(scope, studyTemplateInfo.name),
        );
        return stored?.content || '';
      } catch (error) {
        console.error('[SaveLoadAdapter] getStudyTemplateContent failed:', error);
        return '';
      }
    },

    async removeStudyTemplate(studyTemplateInfo: StudyTemplateMetaInfo): Promise<void> {
      try {
        await studyTemplatesStore.removeItem(buildKey(scope, studyTemplateInfo.name));
      } catch (error) {
        console.error('[SaveLoadAdapter] removeStudyTemplate failed:', error);
      }
    },

    async getDrawingTemplates(toolName: string): Promise<string[]> {
      try {
        const templates: string[] = [];
        const prefix = buildKey(scope, toolName);
        await drawingTemplatesStore.iterate<StoredDrawingTemplate, void>((value, key) => {
          if (key.startsWith(`${prefix}:`)) {
            templates.push(value.templateName);
          }
        });
        return templates;
      } catch (error) {
        console.error('[SaveLoadAdapter] getDrawingTemplates failed:', error);
        return [];
      }
    },

    async loadDrawingTemplate(toolName: string, templateName: string): Promise<string> {
      try {
        const stored = await drawingTemplatesStore.getItem<StoredDrawingTemplate>(
          buildKey(scope, toolName, templateName),
        );
        return stored?.content || '';
      } catch (error) {
        console.error('[SaveLoadAdapter] loadDrawingTemplate failed:', error);
        return '';
      }
    },

    async saveDrawingTemplate(
      toolName: string,
      templateName: string,
      content: string,
    ): Promise<void> {
      try {
        const stored: StoredDrawingTemplate = {
          toolName,
          templateName,
          content,
        };
        await drawingTemplatesStore.setItem(buildKey(scope, toolName, templateName), stored);
      } catch (error) {
        console.error('[SaveLoadAdapter] saveDrawingTemplate failed:', error);
      }
    },

    async removeDrawingTemplate(toolName: string, templateName: string): Promise<void> {
      try {
        await drawingTemplatesStore.removeItem(buildKey(scope, toolName, templateName));
      } catch (error) {
        console.error('[SaveLoadAdapter] removeDrawingTemplate failed:', error);
      }
    },

    async getAllChartTemplates(): Promise<string[]> {
      try {
        const templates: string[] = [];
        await chartTemplatesStore.iterate<StoredChartTemplate, void>((value, key) => {
          if (key.startsWith(`${scope}:`)) {
            templates.push(value.name);
          }
        });
        return templates;
      } catch (error) {
        console.error('[SaveLoadAdapter] getAllChartTemplates failed:', error);
        return [];
      }
    },

    async getChartTemplateContent(templateName: string): Promise<ChartTemplate> {
      try {
        const stored = await chartTemplatesStore.getItem<StoredChartTemplate>(
          buildKey(scope, templateName),
        );
        return { content: stored?.content };
      } catch (error) {
        console.error('[SaveLoadAdapter] getChartTemplateContent failed:', error);
        return { content: undefined };
      }
    },

    async saveChartTemplate(newName: string, theme: ChartTemplateContent): Promise<void> {
      try {
        const stored: StoredChartTemplate = {
          name: newName,
          content: theme,
        };
        await chartTemplatesStore.setItem(buildKey(scope, newName), stored);
      } catch (error) {
        console.error('[SaveLoadAdapter] saveChartTemplate failed:', error);
      }
    },

    async removeChartTemplate(templateName: string): Promise<void> {
      try {
        await chartTemplatesStore.removeItem(buildKey(scope, templateName));
      } catch (error) {
        console.error('[SaveLoadAdapter] removeChartTemplate failed:', error);
      }
    },

    async saveLineToolsAndGroups(
      layoutId: string | undefined,
      chartId: string | number,
      state: LineToolsAndGroupsState,
    ): Promise<void> {
      try {
        const stored: StoredLineTools = {
          layoutId,
          chartId,
          state,
        };
        await lineToolsStore.setItem(buildKey(scope, layoutId ?? 'default', chartId), stored);
      } catch (error) {
        console.error('[SaveLoadAdapter] saveLineToolsAndGroups failed:', error);
      }
    },

    async loadLineToolsAndGroups(
      layoutId: string | undefined,
      chartId: string | number,
      _requestType: LineToolsAndGroupsLoadRequestType,
      _requestContext: LineToolsAndGroupsLoadRequestContext,
    ): Promise<Partial<LineToolsAndGroupsState> | null> {
      try {
        const stored = await lineToolsStore.getItem<StoredLineTools>(
          buildKey(scope, layoutId ?? 'default', chartId),
        );
        return stored?.state || null;
      } catch (error) {
        console.error('[SaveLoadAdapter] loadLineToolsAndGroups failed:', error);
        return null;
      }
    },
  };

  return adapter;
}
